import React, { useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import { Card, CardBody, Row, Col, Badge } from "reactstrap";
import { baseURL } from "../../../../Services/api/baseURL";
import axios from "axios";
import { toast } from "react-toastify";
import { TrendingUp, Package } from "lucide-react";

const TopBoughtCategorySubcategory = ({ customerId }) => {
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);

  const fetchSummary = async () => {
    setLoading(true);
    try {
      const token = JSON.parse(localStorage.getItem("token"));

      const response = await axios.get(
        `${baseURL}/api/customers/get-topBought/${customerId}`,
        {
          headers: { Authorization: `${token}` },
        }
      );

      if (response.status === 200) {
        const data = response.data?.data || {};
        setCategories(data.topCategories || []);
        setSubCategories(data.topSubCategories || []);
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to fetch purchase analytics");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (customerId) fetchSummary();
  }, [customerId]);

  // Enhanced columns for categories with better styling
  const categoryColumns = [
    {
      name: "#",
      selector: (_, index) => index + 1,
      width: "60px",
      center: true,
      cell: (row, index) => (
        <div className="d-flex align-items-center justify-content-center">
          <Badge 
            color={index < 3 ? "success" : "light"} 
            pill 
            className={`fw-bold ${index < 3 ? "text-white" : "text-dark"}`}
          >
            {index + 1}
          </Badge>
        </div>
      ),
    },
    {
      name: "Category",
      selector: (row) => row.categoryName || "-",
      sortable: true,
      grow: 2,
      cell: (row) => (
        <div className="d-flex align-items-center">
          
          <div>
            <div className="fw-semibold text-dark">{row.categoryName || "-"}</div>
          </div>
        </div>
      ),
    },
    {
      name: "Units Sold",
      selector: (row) => row.totalUnits,
      sortable: true,
      center: true,
      width: "150px",
      cell: (row) => (
        <div className="text-center">
          <div className="fw-bold  fs-8">{row.totalUnits.toLocaleString()}</div>
        </div>
      ),
    },
    {
      name: "Orders",
      selector: (row) => row.totalOrders,
      sortable: true,
      center: true,
      width: "100px",
      cell: (row) => (
        <div className="text-center">
         
            {row.totalOrders}
         
        </div>
      ),
    },
  ];

  // Enhanced columns for subcategories
  const subCategoryColumns = [
    {
      name: "#",
      selector: (_, index) => index + 1,
      width: "60px",
      center: true,
      cell: (row, index) => (
        <div className="d-flex align-items-center justify-content-center">
          <Badge 
            color={index < 3 ? "success" : "light"} 
            pill 
            className={`fw-bold ${index < 3 ? "text-white" : "text-dark"}`}
          >
            {index + 1}
          </Badge>
        </div>
      ),
    },
    {
      name: "Subcategory",
      selector: (row) => row.subCategoryName || "-",
      sortable: true,
      grow: 2,
      cell: (row) => (
        <div className="d-flex align-items-center">
          
          <div>
            <div className="fw-semibold text-dark">{row.subCategoryName || "-"}</div>
          </div>
        </div>
      ),
    },
    {
      name: "Units Sold",
      selector: (row) => row.totalUnits,
      sortable: true,
      center: true,
      width: "150px",
      cell: (row) => (
        <div className="text-center">
          <div className="fw-bold  ">{row.totalUnits.toLocaleString()}</div>
        </div>
      ),
    },
    {
      name: "Orders",
      selector: (row) => row.totalOrders,
      sortable: true,
      center: true,
      width: "100px",
      cell: (row) => (
        <div className="text-center">
          
            {row.totalOrders}
          
        </div>
      ),
    },
  ];

  // Custom loading component
  const LoadingComponent = () => (
    <div className="text-center py-5">
      <div className="d-flex justify-content-center align-items-center mb-3">
        <div className="spinner-border text-primary me-3" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <TrendingUp size={24} className="text-primary" />
      </div>
      <h6 className="text-muted mb-1">Analyzing Purchase Patterns</h6>
      <p className="text-muted small mb-0">Fetching top categories and subcategories...</p>
    </div>
  );

  // Custom no data component
  const NoDataComponent = ({ message }) => (
    <div className="text-center py-4">
      <Package size={48} className="text-muted mb-3" />
      <h6 className="text-muted">{message}</h6>
      <p className="text-muted small mb-0">No purchase data available for this customer</p>
    </div>
  );

  // Calculate totals for summary
  const totalCategoryUnits = categories.reduce((sum, cat) => sum + cat.totalUnits, 0);
  const totalSubCategoryUnits = subCategories.reduce((sum, sub) => sum + sub.totalUnits, 0);

  // Custom table styles
  const customTableStyles = {
    headRow: {
      style: {
        backgroundColor: '#f8f9fa',
        borderBottom: '2px solid #dee2e6',
        minHeight: '45px',
        fontWeight: 'bold',
        color: '#495057',
      },
    },
    headCells: {
      style: {
        paddingLeft: '12px',
        paddingRight: '12px',
        fontSize: '0.875rem',
        fontWeight: '600',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
      },
    },
    cells: {
      style: {
        paddingLeft: '12px',
        paddingRight: '12px',
        paddingTop: '12px',
        paddingBottom: '12px',
        fontSize: '0.875rem',
      },
    },
    rows: {
      style: {
        borderBottom: '1px solid #f1f3f4',
        '&:hover': {
          backgroundColor: '#f8f9fa',
          cursor: 'pointer',
          transform: 'translateY(-1px)',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          transition: 'all 0.2s ease-in-out',
        },
      },
    },
  };

  return (
    <Card className="shadow-sm border-0 h-100">
      <CardBody className="p-4">
        {/* Header Section */}
        <div className="d-flex align-items-center justify-content-between mb-4">
          <div className="d-flex align-items-center">
            <div className="me-3">
              <div className="bg-[#d3178a] rounded-circle p-2">
                <TrendingUp size={20} className="text-white" />
              </div>
            </div>
            <div>
              <h5 className="mb-1 fw-bold text-dark">Purchase Analytics</h5>
              <p className="text-muted mb-0 small">Top performing categories and subcategories</p>
            </div>
          </div>
      
        </div>


        {/* Main Content */}
        {loading ? (
          <LoadingComponent />
        ) : (
          <Row className="g-4">
            {/* Categories Table */}
            <Col lg={6}>
              <div className="border rounded-3 p-3 h-100">
                <div className="d-flex align-items-center mb-3">
                  <Package size={18} className="text-[#d3178a] me-2" />
                  <h6 className="mb-0 fw-bold text-dark">Top Categories</h6>
                 
                </div>
                <DataTable
                  data={categories}
                  columns={categoryColumns}
                  pagination={categories.length > 10}
                  paginationPerPage={10}
                  paginationRowsPerPageOptions={[5, 10, 15, 20]}
                  highlightOnHover
                  striped
                  responsive
                  noDataComponent={<NoDataComponent message="No category data available" />}
                  customStyles={customTableStyles}
                  dense
                />
              </div>
            </Col>

            {/* Subcategories Table */}
            <Col lg={6}>
              <div className="border rounded-3 p-3 h-100">
                <div className="d-flex align-items-center mb-3">
                  <Package size={18} className="text-[#d3178a] me-2" />
                  <h6 className="mb-0 fw-bold text-dark">Top Subcategories</h6>
             
                </div>
                <DataTable
                  data={subCategories}
                  columns={subCategoryColumns}
                  pagination={subCategories.length > 10}
                  paginationPerPage={10}
                  paginationRowsPerPageOptions={[5, 10, 15, 20]}
                  highlightOnHover
                  striped
                  responsive
                  noDataComponent={<NoDataComponent message="No subcategory data available" />}
                  customStyles={customTableStyles}
                  dense
                />
              </div>
            </Col>
          </Row>
        )}
      </CardBody>
    </Card>
  );
};

export default TopBoughtCategorySubcategory;